/**
 * Mise à jour guidée des anciens APK (< 1.5.9.33) : voir src/lib/guided-update/policy.ts.
 *
 * Une proposition au plus par jour et par version, caisse au repos ; jamais pendant une
 * vente (la fenêtre disparaît dès qu'un article entre au panier, la fermeture de l'appli
 * attend que la caisse soit libre). Respecte le verrou updates.autoupdate.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { DeviceRemoteApi } from '@/api/devices/DeviceRemoteApi';
import {
  canCloseApp,
  closeApp,
  fetchGuidedManifest,
  readNativeVersionCode,
  subscribeUpdaterEvents,
  triggerNativeUpdate,
} from '@/lib/guided-update/native-bridge';
import {
  EMPTY_TRACK,
  GUIDED_STORAGE_KEY,
  GUIDED_UPDATE_MAX_CODE,
  decideGuidedOffer,
  isGuidedCandidate,
  nextGuidedStep,
  parseGuidedRecord,
  recentlyRestarted,
  recordAfterOffer,
  trackUpdaterEvent,
  type DownloadTrack,
  type GuidedManifest,
  type GuidedRecord,
} from '@/lib/guided-update/policy';
import { isWebUpdateShown, markWebUpdateShown } from '@/lib/guided-update/session';
import {
  createBrowserIdleProbe,
  evaluateCaisseIdle,
  type CaisseIdleProbe,
} from '@/lib/remote-commands/caisse-idle';

export type GuidedView =
  | { phase: 'hidden' }
  | { phase: 'offer' }
  | { phase: 'downloading'; percent: number | null }
  | { phase: 'ready'; canClose: boolean }
  | { phase: 'restart'; canClose: boolean }
  | { phase: 'installing' }
  | { phase: 'blocked' };

const FIRST_CHECK_MS = 20_000;
const OFFER_CHECK_MS = 30_000;
const WATCH_MS = 1_000;
/** Temps de lecture du message avant la fermeture de l'appli. */
const CLOSE_DELAY_MS = 8_000;
const HIDDEN: GuidedView = { phase: 'hidden' };

function readRecord(): GuidedRecord | null {
  try {
    return parseGuidedRecord(window.localStorage.getItem(GUIDED_STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeRecord(record: GuidedRecord): void {
  try {
    window.localStorage.setItem(GUIDED_STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* stockage indisponible : la proposition reviendra au prochain lancement */
  }
}

/** Vente en cours, fenêtre ouverte ou écran monnayeur (le dernier toucher ne compte pas). */
function isCaisseBusy(probe: CaisseIdleProbe): boolean {
  return !evaluateCaisseIdle({ ...probe, lastInteractionAt: () => 0 }).idle;
}

async function isUpdateLocked(): Promise<boolean> {
  try {
    const locked = await DeviceRemoteApi.isAutoUpdateLocked();
    return locked.ok && locked.value;
  } catch {
    return false; // même règle que useAutoUpdate : vérification en échec = pas de verrou
  }
}

function stepView(step: ReturnType<typeof nextGuidedStep>, track: DownloadTrack): GuidedView {
  if (step === 'downloading') return { phase: 'downloading', percent: track.percent };
  if (step === 'ready' || step === 'restart') return { phase: step, canClose: canCloseApp() };
  return { phase: step };
}

type OfferContext = { code: number; manifest: GuidedManifest | null; probe: CaisseIdleProbe };

/** Proposition si tout est réuni ; renvoie le manifeste lu pour le réutiliser. */
async function tryOffer(ctx: OfferContext, show: () => void): Promise<GuidedManifest | null> {
  if (isWebUpdateShown()) return ctx.manifest;
  const manifest = ctx.manifest ?? (await fetchGuidedManifest());
  if (!manifest || !isGuidedCandidate(ctx.code, manifest)) return manifest;
  if (!evaluateCaisseIdle(ctx.probe).idle) return manifest;
  const locked = await isUpdateLocked();
  const record = readRecord();
  const now = Date.now();
  const offer = decideGuidedOffer({
    nativeCode: ctx.code,
    manifest,
    locked,
    idle: evaluateCaisseIdle(ctx.probe).idle,
    shownThisSession: isWebUpdateShown(),
    record,
    now,
  });
  if (!offer.show) return manifest;
  writeRecord(recordAfterOffer(record, manifest.versionCode, offer.resume, now));
  markWebUpdateShown();
  show();
  return manifest;
}

type GuidedSession = {
  manifest: GuidedManifest | null;
  probe: CaisseIdleProbe | null;
  track: DownloadTrack;
  clickedAt: number;
};

export function useGuidedUpdate(): {
  view: GuidedView;
  accept: () => void;
  dismiss: () => void;
} {
  const [view, setView] = useState<GuidedView>(HIDDEN);
  const session = useRef<GuidedSession>({
    manifest: null,
    probe: null,
    track: EMPTY_TRACK,
    clickedAt: 0,
  }).current;

  useEffect(() => {
    const code = readNativeVersionCode();
    // Hors APK ou APK qui installe seul (≥ 1.5.9.33) : rien à faire, aucun suivi.
    if (code === null || code >= GUIDED_UPDATE_MAX_CODE) return;
    const probe = createBrowserIdleProbe();
    session.probe = probe;
    const unsubscribe = subscribeUpdaterEvents((event) => {
      session.track = trackUpdaterEvent(session.track, event, Date.now());
    });
    let cancelled = false;
    const check = () => {
      if (isWebUpdateShown()) return;
      void tryOffer({ code, manifest: session.manifest, probe }, () => {
        if (!cancelled) setView({ phase: 'offer' });
      }).then((manifest) => {
        session.manifest = manifest;
      });
    };
    const first = window.setTimeout(check, FIRST_CHECK_MS);
    const interval = window.setInterval(check, OFFER_CHECK_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(first);
      window.clearInterval(interval);
      unsubscribe();
    };
  }, [session]);

  useGuidedWatchers(view, setView, session);

  const accept = useCallback(() => {
    session.clickedAt = Date.now();
    if (!session.track.ready && !session.track.installing) triggerNativeUpdate();
    setView({ phase: 'downloading', percent: session.track.percent });
  }, [session]);
  const dismiss = useCallback(() => setView(HIDDEN), []);

  return { view, accept, dismiss };
}

function sameView(a: GuidedView, b: GuidedView): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function useGuidedWatchers(
  view: GuidedView,
  setView: (update: (current: GuidedView) => GuidedView) => void,
  session: GuidedSession,
): void {
  const { phase } = view;

  // Proposition affichée : elle s'efface dès que la caisse sert (panier, fenêtre, monnayeur).
  useEffect(() => {
    if (phase !== 'offer') return;
    const id = window.setInterval(() => {
      if (session.probe && isCaisseBusy(session.probe)) setView(() => HIDDEN);
    }, WATCH_MS);
    return () => window.clearInterval(id);
  }, [phase, session, setView]);

  // Après « Mettre à jour » : suit le téléchargement jusqu'à l'étape suivante.
  useEffect(() => {
    if (phase !== 'downloading') return;
    const id = window.setInterval(() => {
      const now = Date.now();
      const code = session.manifest?.versionCode ?? 0;
      const restarted = recentlyRestarted(readRecord(), code, now);
      const step = nextGuidedStep(session.track, session.clickedAt, now, restarted);
      const next = stepView(step, session.track);
      setView((current) => (sameView(current, next) ? current : next));
    }, WATCH_MS);
    return () => window.clearInterval(id);
  }, [phase, session, setView]);

  // Fichier prêt : fermeture de l'appli après lecture du message, caisse libre uniquement.
  const closable = 'canClose' in view && view.canClose;
  useEffect(() => {
    if (!closable) return;
    const startedAt = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - startedAt < CLOSE_DELAY_MS) return;
      if (session.probe && isCaisseBusy(session.probe)) return;
      window.clearInterval(id);
      const now = Date.now();
      const record = readRecord();
      const code = session.manifest?.versionCode ?? record?.code ?? 0;
      writeRecord({ ...(record ?? { code, shownAt: now }), restartedAt: now });
      closeApp();
    }, WATCH_MS);
    return () => window.clearInterval(id);
  }, [closable, session]);
}
